import type { Volume, Bonus, PainPoint, Testimonial, Faq, Buyer } from "../types/content";

export const CHECKOUT_URL = "https://insulin-en.impultienda.ar/checkout/";

export const images = {
  hero: "https://cdn.magicpatterns.com/uploads/tamL6FXkNPuoBY7siHyAuo/INSULIN-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/9NMpginKFpMKZYTrDuRKyW/Dr._Henry_Wells_-_DIABETES.png",
};

export const patientFactors = [
  "Medical and nutritional history",
  "Eating patterns and lifestyle habits",
  "Relevant laboratory results",
  "Existing health conditions",
  "Previous nutrition interventions",
  "Individual goals and preferences",
  "Follow-up and documentation requirements",
];

export const beforeItems = [
  "You spend valuable time searching for the right reference before a consultation.",
  "You repeatedly review the same topics because your information is scattered across different sources.",
  "You want a more consistent structure for organizing nutrition assessments.",
  "You need a clearer way to document eating patterns, lifestyle factors, and relevant clinical information.",
  "You want to organize nutrition discussions without starting from scratch every time.",
  "You find it difficult to keep track of follow-up considerations and outstanding questions.",
];

export const afterItems = [
  "Organize the major stages of insulin resistance nutrition care.",
  "Navigate relevant topics through a visual workflow framework.",
  "Structure the information you need to review before and during consultations.",
  "Organize nutrition goals and patient-reported lifestyle information.",
  "Document relevant considerations using dedicated worksheets.",
  "Prepare more consistent follow-up reviews.",
];

export const painPoints: PainPoint[] = [
  {
    title: "You spend too much time preparing for insulin resistance consultations.",
    body: "You know the information is available, but finding and organizing the relevant material takes time.",
  },
  {
    title: "You want a more systematic nutrition assessment process.",
    body: "You need a practical framework for organizing patient history, eating patterns, lifestyle factors, relevant clinical information, and individual goals.",
  },
  {
    title: "You want a clearer way to organize nutrition interventions.",
    body: "You need a structured method for documenting nutrition priorities, patient preferences, previous approaches, and questions requiring further evaluation.",
  },
  {
    title: "You need to organize the different factors associated with insulin resistance.",
    body: "You want a dedicated place to document relevant metabolic information, nutrition-related considerations, lifestyle habits, and existing health conditions.",
  },
  {
    title: "You want more consistent follow-up documentation.",
    body: "You need a practical way to record patient-reported progress, outstanding questions, relevant monitoring considerations, and topics to revisit during future consultations.",
  },
  {
    title: "You're tired of creating your own resources from scratch.",
    body: "You would rather work with a structured collection of professional templates than repeatedly rebuild your notes and checklists.",
  },
];

export const volumes: Volume[] = [
  {
    numeral: "I",
    title: "The Insulin Resistance Assessment Blueprint™",
    subtitle: "Organize the Initial Nutrition Assessment",
    intro: [
      "I created this volume to help you organize the information relevant to an initial nutrition assessment when insulin resistance is a concern.",
      "Instead of gathering information from scattered notes and creating your own forms, you'll have a structured framework to guide your documentation.",
    ],
    items: [
      "A structured overview of the initial nutrition assessment.",
      "A patient history and nutrition background organizer.",
      "An eating patterns and lifestyle habits worksheet.",
      "A framework for documenting relevant clinical information.",
      "A checklist for organizing available laboratory results.",
      "A patient goals and preferences worksheet.",
      "A template for recording outstanding questions.",
      "A guide to organizing information requiring further evaluation.",
    ],
    objective:
      "Help you organize the information relevant to an initial insulin resistance nutrition assessment without having to build your own framework from scratch.",
    disclaimer:
      "The materials support clinical organization and do not replace individualized assessment, established diagnostic criteria, or professional judgment.",
  },
  {
    numeral: "II",
    title: "The Metabolic Health Review Blueprint™",
    subtitle: "Organize Metabolic Information and Related Health Considerations",
    intro: [
      "Insulin resistance may be considered alongside several metabolic and health-related factors.",
      "I designed this volume to help you organize those considerations in one place, making it easier to document the information available and identify questions that require further review.",
    ],
    items: [
      "A metabolic health review framework.",
      "A laboratory information organizer.",
      "A worksheet for documenting relevant clinical measurements.",
      "A checklist for recording existing health conditions.",
      "A lifestyle and health history organizer.",
      "A framework for documenting relevant cardiovascular and metabolic considerations.",
      "A template for organizing questions requiring further evaluation.",
      "A reference verification checklist.",
    ],
    objective: "Give relevant metabolic information a clear place within your nutrition consultation workflow.",
    disclaimer:
      "Clinical interpretation and subsequent actions must be based on the patient's circumstances, current evidence, and applicable professional guidance.",
  },
  {
    numeral: "III",
    title: "The Insulin Resistance Nutrition Planning Blueprint™",
    subtitle: "Structure Individualized Nutrition Consultations",
    intro: [
      "Nutrition care should never be reduced to a one-size-fits-all meal plan.",
      "A patient's history, food preferences, daily routine, previous experiences, health status, and individual goals may all influence the nutrition care process.",
      "I created this volume to help you organize these considerations before and during nutrition consultations.",
    ],
    items: [
      "A framework for documenting individualized nutrition goals.",
      "A patient food preferences and priorities worksheet.",
      "An eating patterns organizer.",
      "A previous nutrition interventions tracker.",
      "A matrix for organizing relevant nutrition considerations.",
      "A worksheet for documenting lifestyle and behavioral factors.",
      "A shared decision-making worksheet.",
      "A checklist for recording questions requiring further clinical review.",
    ],
    objective:
      "Help you structure nutrition discussions and document relevant considerations without relying on memory alone.",
    disclaimer:
      "Individualized nutrition recommendations remain a professional decision informed by the patient's circumstances, clinical needs, and current evidence.",
  },
  {
    numeral: "IV",
    title: "The Advanced Metabolic Nutrition Blueprint™",
    subtitle: "Organize Complex Cases and Additional Clinical Considerations",
    intro: [
      "Some patients present with multiple health conditions, complex nutritional needs, previous treatment experiences, or additional considerations that require a more detailed review.",
      "I designed this volume to help you organize complex cases without turning your consultation notes into a collection of disconnected documents.",
    ],
    items: [
      "A complex case assessment worksheet.",
      "A framework for organizing relevant medical and nutrition history.",
      "A review checklist for coexisting health conditions.",
      "A template for documenting previous nutrition interventions.",
      "A nutrition considerations matrix.",
      "A worksheet for recording questions requiring professional verification.",
      "A reference-tracking sheet.",
      "A checklist for organizing potential referral or interdisciplinary collaboration considerations.",
    ],
    objective:
      "Help you organize complex insulin resistance nutrition consultations and identify questions that require further evaluation or collaboration with other healthcare professionals.",
    disclaimer:
      "The material does not independently determine diagnoses, treatment eligibility, or clinical management.",
  },
  {
    numeral: "V",
    title: "The Insulin Resistance Progress Review Blueprint™",
    subtitle: "Organize Nutrition Follow-Up and Progress Reviews",
    intro: [
      "Starting a nutrition plan is only one part of the care process.",
      "Follow-up consultations may require you to review eating patterns, patient-reported progress, barriers, preferences, changes in routine, and other relevant information.",
      "I created this volume to give those conversations a clearer structure.",
    ],
    items: [
      "A nutrition progress review worksheet.",
      "A patient-reported progress organizer.",
      "A template for documenting changes in eating patterns and lifestyle habits.",
      "A checklist for organizing relevant tolerability and safety questions.",
      "A framework for documenting barriers and challenges.",
      "A patient goals review sheet.",
      "A reference verification checklist.",
      "A structured follow-up documentation template.",
    ],
    objective: "Make nutrition progress reviews easier to organize, document, and prepare.",
    disclaimer:
      "Clinical decisions about modifying nutrition care must be based on individualized assessment, professional judgment, and current evidence.",
  },
  {
    numeral: "VI",
    title: "The Long-Term Metabolic Health Blueprint™",
    subtitle: "Organize Continuity, Follow-Up, and Ongoing Nutrition Care",
    intro: [
      "Insulin resistance nutrition care often extends beyond a single consultation.",
      "Patients may need ongoing support as their routines, goals, circumstances, and health needs change.",
      "I created this volume to help you maintain a clearer overview of the nutrition care process and prepare future reviews more consistently.",
    ],
    items: [
      "A long-term follow-up planning framework.",
      "A periodic review checklist.",
      "A patient goals reassessment worksheet.",
      "A template for documenting outstanding issues.",
      "A follow-up preparation sheet.",
      "A framework for organizing relevant health information.",
      "A continuity-of-care documentation template.",
      "A worksheet for preparing the next consultation.",
    ],
    objective: "Help you establish a more structured process for organizing ongoing insulin resistance nutrition care.",
    disclaimer:
      "Follow-up intervals and monitoring requirements must be individualized according to the patient's circumstances and applicable professional guidance.",
  },
];

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "The Insulin Resistance Assessment Worksheet",
    description: "A structured worksheet for organizing the information relevant to an initial nutrition assessment.",
    items: [
      "Patient history organizer.",
      "Eating patterns documentation fields.",
      "Relevant health history checklist.",
      "Available laboratory information section.",
      "Individual goals and preferences fields.",
      "Outstanding information tracker.",
      "Clinical documentation template.",
    ],
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/iv6kNeQjqUvJe83h3zBENj/BNS_1_INSULIN-EN.png",
  },
  {
    number: 2,
    title: "The Metabolic Health Review Matrix",
    description: "A practical tool for organizing relevant metabolic information and associated health considerations.",
    items: [
      "Metabolic health review worksheet.",
      "Relevant laboratory results organizer.",
      "Clinical measurements documentation fields.",
      "Existing health conditions matrix.",
      "Additional assessment questions tracker.",
      "Reference verification checklist.",
    ],
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/v1QiBDibhio2cZq5aAT7z9/BNS_2_INSULIN-EN.png",
  },
  {
    number: 3,
    title: "The Nutrition Consultation Planner",
    description: "A practical worksheet for structuring patient-centered conversations about nutrition and lifestyle.",
    items: [
      "Patient goals and preferences worksheet.",
      "Eating patterns organizer.",
      "Previous nutrition interventions tracker.",
      "Shared decision-making framework.",
      "Nutrition discussion checklist.",
      "Questions requiring further review section.",
      "Consultation documentation template.",
    ],
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/nFHXFZbkNKE23LWzBjECfP/BNS_3_INSULIN-EN.png",
  },
  {
    number: 4,
    title: "The Insulin Resistance Progress Review Tracker",
    description: "A structured tool for organizing follow-up discussions and nutrition progress reviews.",
    items: [
      "Nutrition progress review worksheet.",
      "Patient-reported progress tracker.",
      "Lifestyle and eating patterns documentation fields.",
      "Barriers and challenges review.",
      "Outstanding questions register.",
      "Next review planning sheet.",
    ],
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/wJaDrQZ1nkhN9Ba1aEfRzL/BNS_4_ISULIN-EN.png",
  },
  {
    number: 5,
    title: "The Long-Term Metabolic Health Checklist",
    description: "A practical checklist for organizing periodic reviews and preparing future nutrition consultations.",
    items: [
      "Periodic review checklist.",
      "Patient goals reassessment worksheet.",
      "Follow-up task organizer.",
      "Outstanding issues register.",
      "Reference verification section.",
      "Next consultation preparation template.",
    ],
    value: 15,
    image: "https://cdn.magicpatterns.com/uploads/2iBQR4Zi6A4ukSBhh1qges/BNS_5_INSULIN-EN.png",
  },
];

export const testimonials: Testimonial[] = [
  {
    name: "Rachel Thompson, RD",
    role: "Registered Dietitian",
    location: "Austin, Texas",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/50ab266b-2a27-4aa5-a03e-ccae579b75ad.jpg",
    quote:
      "I used to rebuild my intake forms for every new insulin resistance client. Now I open Volume I, print the assessment worksheet, and every first consultation follows the same clear structure.",
  },
  {
    name: "Daniel Morgan",
    role: "Clinical Nutritionist",
    location: "Manchester, UK",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/33baef5e-9e67-45e0-851b-b6e593cf8710.jpg",
    quote:
      "The visual framework is what won me over. Before a follow-up I jump straight to the progress review section instead of digging through folders of old notes.",
  },
  {
    name: "Aisha Bennett, MS, RDN",
    role: "Nutrition Consultant",
    location: "Atlanta, Georgia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/31707a56-4840-49c4-b8b3-aed393966ba6.jpg",
    quote:
      "The shared decision-making worksheet changed how I run nutrition conversations. My notes are finally consistent from one patient to the next.",
  },
  {
    name: "Linda Carter, RD",
    role: "Dietitian, 20+ years in practice",
    location: "Toronto, Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/ee1b10e2-9cac-43e8-ace5-4bfc1f46e292.jpg",
    quote:
      "I didn't need more theory — I needed organization. For $27 I stopped spending my weekends building checklists and templates from scratch.",
  },
];

export const faqs: Faq[] = [
  {
    question: "How will I receive Insulin Resistance Nutrition Blueprint™?",
    answer: [
      "After your payment is confirmed, you'll receive instructions for accessing and downloading the included digital materials.",
      "The collection is designed for digital use and can be printed for personal professional reference.",
    ],
  },
  {
    question: "Is the material suitable for healthcare professionals in different countries?",
    answer: [
      "The system focuses on organizing nutrition assessments, consultations, documentation, and follow-up.",
      "However, clinical guidelines, professional scopes of practice, and applicable requirements vary between countries. You should adapt the materials to your local standards and current professional guidance.",
    ],
  },
  {
    question: "Do I need to be a specialist in insulin resistance?",
    answer: [
      "The collection is intended for qualified nutrition professionals and other appropriately qualified healthcare professionals who work within their professional scope.",
      "It is a reference and organization resource, not a substitute for clinical training or specialist supervision.",
    ],
  },
  {
    question: "Does the product provide individualized treatment recommendations?",
    answer: [
      "No. The materials help you organize information, document relevant considerations, and structure nutrition consultations.",
      "They do not independently diagnose insulin resistance or determine individualized treatment.",
    ],
  },
  {
    question: "Are the files downloadable?",
    answer: ["Yes. The included materials are provided for digital access, subject to the delivery format specified at checkout."],
  },
  {
    question: "Can I print the worksheets?",
    answer: ["Yes. The worksheets and checklists are designed to support digital consultation and printing for your own professional workflow."],
  },
  {
    question: "Is this a recurring subscription?",
    answer: ["No. The advertised price is a one-time payment of $27 USD. There are no recurring subscription fees for this offer."],
  },
  {
    question: "Does Insulin Resistance Nutrition Blueprint™ replace official clinical guidelines?",
    answer: [
      "No. It is a clinical organization and educational reference resource.",
      "Clinical decisions must be based on individualized assessment, current evidence, applicable guidelines, and professional judgment.",
    ],
  },
  {
    question: "What if the product isn't right for me?",
    answer: [
      "You can review the materials during the seven-day guarantee period.",
      "If you decide the collection is not suitable for your needs, you can contact us to request a refund under the stated guarantee terms.",
    ],
  },
];

export const buyers: Buyer[] = [
  { name: "María González", country: "Mexico" },
  { name: "Carlos Ramírez", country: "Colombia" },
  { name: "Lucía Fernández", country: "Spain" },
  { name: "Valentina Rojas", country: "Chile" },
  { name: "Javier Morales", country: "Argentina" },
  { name: "Camila Torres", country: "Peru" },
  { name: "Andrés Castillo", country: "Ecuador" },
  { name: "Sofía Herrera", country: "Uruguay" },
  { name: "Diego Navarro", country: "Costa Rica" },
  { name: "Isabella Vargas", country: "Dominican Republic" },
  { name: "Mateo Jiménez", country: "Guatemala" },
  { name: "Daniela Pérez", country: "Venezuela" },
  { name: "Gabriela Ortiz", country: "Panama" },
  { name: "Fernando Ruiz", country: "Bolivia" },
  { name: "Paula Medina", country: "Paraguay" },
  { name: "Ricardo Salazar", country: "Puerto Rico" },
];
